import { useState, Fragment } from 'react';
import type { SectionTimelineRow } from '../lib/types';
import { ScorePill } from './ScorePill';
import { formatScore } from '../lib/format';
import styles from './SectionTimeline.module.css';

interface Props {
  sections: SectionTimelineRow[];
  className?: string;
}

const COLLAPSE_THRESHOLD = 8;
const HEAD_KEEP = 3;
const TAIL_KEEP = 2;

export function SectionTimeline({ sections, className }: Props) {
  const [expanded, setExpanded] = useState(false);
  const collapsable = sections.length > COLLAPSE_THRESHOLD && !expanded;
  const head = collapsable ? sections.slice(0, HEAD_KEEP) : sections;
  const tail = collapsable ? sections.slice(-TAIL_KEEP) : [];
  const hiddenCount = sections.length - head.length - tail.length;

  const renderRow = (s: SectionTimelineRow) => (
    <li key={s.idx} className={styles.item}>
      <div
        className={[styles.circle, s.verdict === 'MIXED' ? styles.dashed : ''].join(' ')}
        aria-hidden="true"
      >
        <span className={styles.circleNum}>{s.idx}</span>
      </div>
      <div className={styles.body}>
        <div className={styles.head}>
          <h3 className={styles.title}>{s.title}</h3>
          <div className={styles.headRight}>
            <ScorePill verdict={s.verdict} score={s.score} size="sm" />
          </div>
        </div>
        <div className={styles.meta}>
          <span className={[styles.range, 'mono'].join(' ')}>{s.range}</span>
          <span className={styles.metaScore}>Score {formatScore(s.score)}</span>
          {s.tags.length > 0 && (
            <ul className={styles.tags}>
              {s.tags.map((t) => (
                <li key={t} className={styles.tag}>{t}</li>
              ))}
            </ul>
          )}
        </div>
        <p className={styles.summary}>{s.summary}</p>
      </div>
    </li>
  );

  return (
    <ol className={[styles.timeline, className].filter(Boolean).join(' ')}>
      <Fragment>{head.map(renderRow)}</Fragment>
      {collapsable && hiddenCount > 0 && (
        <li className={styles.expandRow}>
          <button type="button" className={styles.expandBtn} onClick={() => setExpanded(true)}>
            Show {hiddenCount} hidden section{hiddenCount === 1 ? '' : 's'}
          </button>
        </li>
      )}
      {tail.map(renderRow)}
    </ol>
  );
}
